import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AssetTable } from "@/components/v2/AssetTable";
import { ChartCard } from "@/components/v2/ChartCard";
import { EmptyState } from "@/components/v2/EmptyState";
import { InlineNativeSelect } from "@/components/v2/InlineNativeSelect";
import { SectionBlock } from "@/components/v2/SectionBlock";
import { StatGridCard } from "@/components/v2/StatGridCard";
import { LineMetricChart, PriceLineChart } from "@/components/v2/charts";
import { useMarketHistory, useMarketStats } from "@/hooks/useHip3Analytics";
import { useNormalizedMarkets } from "@/hooks/useNormalizedMarkets";
import { formatCompact, formatFunding, formatPrice, formatSpreadBps } from "@/lib/formatters";
import { marketNotionalOi, normalizeMarketId, normalizeProvider } from "@/lib/market-utils";
import type { MarketInterval, MarketWindow } from "@/api/hip3";

const intervalByWindow: Record<MarketWindow, MarketInterval[]> = {
  "24h": ["5m", "1h", "4h"],
  "1d": ["5m", "1h", "4h", "1d"],
  "7d": ["1h", "4h", "1d"],
  "30d": ["4h", "1d"],
};

function normalizeInterval(window: MarketWindow, interval: MarketInterval): MarketInterval {
  if (!intervalByWindow[window].includes(interval)) {
    return intervalByWindow[window][0];
  }
  return interval;
}

export function MarketDetailPage() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const provider = normalizeProvider(params.provider ?? "");
  const marketId = normalizeMarketId(provider, params.marketId ?? "");

  const windowRaw = (searchParams.get("window") as MarketWindow | null) ?? "7d";
  const intervalRaw = (searchParams.get("interval") as MarketInterval | null) ?? "1h";
  const interval = normalizeInterval(windowRaw, intervalRaw);

  const marketsQuery = useNormalizedMarkets();
  const selectedMarket = useMemo(
    () =>
      marketsQuery.markets.find(
        (market) =>
          normalizeProvider(market.provider) === provider &&
          normalizeMarketId(provider, market.marketId) === marketId
      ),
    [marketsQuery.markets, provider, marketId]
  );

  const statsQuery = useMarketStats({ provider, marketId, window: windowRaw, enabled: Boolean(provider && marketId) });
  const historyQuery = useMarketHistory({
    provider,
    marketId,
    window: windowRaw,
    interval,
    enabled: Boolean(provider && marketId),
  });

  const sameAssetMarkets = useMemo(() => {
    if (!selectedMarket) return [];
    return marketsQuery.markets
      .filter((market) => market.baseAsset === selectedMarket.baseAsset)
      .sort((a, b) => (b.volume24h ?? 0) - (a.volume24h ?? 0));
  }, [marketsQuery.markets, selectedMarket]);

  const oiSeries = useMemo(
    () =>
      historyQuery.data?.candles.map((candle) => ({
        t: candle.t,
        value:
          typeof candle.openInterest === "number" &&
            Number.isFinite(candle.openInterest) &&
            typeof candle.close === "number" &&
            Number.isFinite(candle.close)
            ? candle.openInterest * candle.close
            : null,
      })) ?? [],
    [historyQuery.data?.candles]
  );

  const priceSeries = useMemo(
    () =>
      historyQuery.data?.candles.map((candle) => ({
        t: candle.t,
        value: candle.close,
      })) ?? [],
    [historyQuery.data?.candles]
  );

  const fundingSeries = useMemo(
    () =>
      historyQuery.data?.candles.map((candle) => ({
        t: candle.t,
        value: candle.fundingAvg,
      })) ?? [],
    [historyQuery.data?.candles]
  );

  const spreadSeries = useMemo(
    () =>
      historyQuery.data?.candles.map((candle) => ({
        t: candle.t,
        value: candle.spreadBpsAvg,
      })) ?? [],
    [historyQuery.data?.candles]
  );

  const hasPricePoints = priceSeries.some(
    (point) => typeof point.value === "number" && Number.isFinite(point.value)
  );
  const hasOiPoints = oiSeries.some(
    (point) => typeof point.value === "number" && Number.isFinite(point.value)
  );
  const hasFundingPoints = fundingSeries.some(
    (point) => typeof point.value === "number" && Number.isFinite(point.value)
  );
  const hasSpreadPoints = spreadSeries.some(
    (point) => typeof point.value === "number" && Number.isFinite(point.value)
  );
  const oiWindowPoints = oiSeries
    .map((point) => point.value)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const currentOi = oiWindowPoints.length > 0
    ? oiWindowPoints[oiWindowPoints.length - 1]
    : selectedMarket
      ? marketNotionalOi(selectedMarket)
      : null;
  const oiChangePct =
    oiWindowPoints.length >= 2 && oiWindowPoints[0] > 0
      ? ((oiWindowPoints[oiWindowPoints.length - 1] - oiWindowPoints[0]) / oiWindowPoints[0]) *
      100
      : null;

  if (!provider || !marketId) {
    return <EmptyState title="Market not found" description="Missing provider or market identifier." />;
  }

  return (
    <div className="space-y-6">
      <SectionBlock
        title={`${selectedMarket?.baseAsset ?? marketId} / ${selectedMarket?.quoteAsset ?? "USDC"}`}
        subtitle={`${provider.toUpperCase()} market analytics and cross-provider context.`}
        actions={
          <div className="flex items-center gap-2">
            <InlineNativeSelect
              value={windowRaw}
              onChange={(event) => {
                const nextWindow = event.target.value as MarketWindow;
                const nextInterval = normalizeInterval(nextWindow, interval);
                const next = new URLSearchParams(searchParams);
                next.set("window", nextWindow);
                next.set("interval", nextInterval);
                setSearchParams(next, { replace: true });
              }}
              className="h-8 border border-border/70 bg-card pl-2 text-xs font-mono"
            >
              <option value="1d">1d</option>
              <option value="7d">7d</option>
              <option value="30d">30d</option>
            </InlineNativeSelect>
            <InlineNativeSelect
              value={interval}
              onChange={(event) => {
                const next = new URLSearchParams(searchParams);
                next.set("interval", event.target.value);
                setSearchParams(next, { replace: true });
              }}
              className="h-8 border border-border/70 bg-card pl-2 text-xs font-mono"
            >
              {intervalByWindow[windowRaw].map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </InlineNativeSelect>
          </div>
        }
      >
        <div className="grid gap-3 md:grid-cols-5">
          <StatGridCard
            label="Price"
            value={`$${formatPrice(selectedMarket?.currentPrice)}`}
            delta={statsQuery.data?.price.changePct}
          />
          <StatGridCard
            label="Open Interest"
            value={formatCompact(currentOi, "$")}
            delta={oiChangePct}
            sub={`${windowRaw.toUpperCase()} window`}
          />
          <StatGridCard
            label="Funding"
            value={formatFunding(statsQuery.data?.funding.mean)}
            sub="Window average"
          />
          <StatGridCard
            label="Spread"
            value={formatSpreadBps(statsQuery.data?.spread.avgBps)}
            sub={`Trend ${statsQuery.data?.spread.trend ?? "—"}`}
          />
          <StatGridCard
            label="24h Volume"
            value={formatCompact(selectedMarket?.volume24h, "$")}
            sub="Latest market snapshot"
          />
        </div>
      </SectionBlock>

      <SectionBlock title="Market Charts">
        <div className="grid gap-3 md:grid-cols-2">
          <ChartCard title="Price Over Time" loading={historyQuery.isLoading}>
            {hasPricePoints ? (
              <PriceLineChart data={priceSeries} providerLabel={provider.toUpperCase()} />
            ) : (
              <ChartMessage
                title="No price history"
                description="No valid price points are available for this market, window, and interval."
              />
            )}
          </ChartCard>
          <ChartCard title="Open Interest Over Time" loading={historyQuery.isLoading}>
            {hasOiPoints ? (
              <LineMetricChart
                data={oiSeries}
                valueFormatter={(value) => formatCompact(value, "$")}
                tooltipVariant="contrast"
              />
            ) : (
              <ChartMessage
                title="No OI history"
                description="No valid open interest points are available for this market, window, and interval."
              />
            )}
          </ChartCard>
          <ChartCard title="Funding Over Time" loading={historyQuery.isLoading}>
            {hasFundingPoints ? (
              <LineMetricChart
                data={fundingSeries}
                valueFormatter={(value) => formatFunding(value)}
                tooltipVariant="contrast"
              />
            ) : (
              <ChartMessage
                title="No funding history"
                description="No valid funding points are available for this market, window, and interval."
              />
            )}
          </ChartCard>
          <ChartCard title="Spread Over Time" loading={historyQuery.isLoading}>
            {hasSpreadPoints ? (
              <LineMetricChart
                data={spreadSeries}
                valueFormatter={(value) => `${value.toFixed(2)} bps`}
                tooltipVariant="contrast"
              />
            ) : (
              <ChartMessage
                title="No spread history"
                description="No valid spread points are available for this market, window, and interval."
              />
            )}
          </ChartCard>
        </div>
      </SectionBlock>

      <SectionBlock
        title="Provider Comparison"
        subtitle="Same asset across providers for side-by-side evaluation."
      >
        <AssetTable markets={sameAssetMarkets} title="Same-Asset Markets Across Providers" />
      </SectionBlock>
    </div>
  );
}

function ChartMessage({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-[280px] w-full items-center justify-center border border-border/70 bg-background/45 px-4 text-center">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
